import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SubscriptionService } from 'src/app/features/subscription/subscription.service';
import { lastValueFrom } from 'rxjs';
import { DynamicConfirmationComponent } from '../dynamic-confirmation/dynamic-confirmation.component';

@Component({
  selector: 'app-global-search-stakeholder',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    DynamicConfirmationComponent,
  ],
  templateUrl: './global-search-stakeholder.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./global-search-stakeholder.component.scss'],
})
export class GlobalSearchStakeholderComponent {
  @ViewChild('lastNameInput') lastNameInput!: ElementRef;
  @ViewChild('zipCodeInput') zipCodeInput!: ElementRef;
  @ViewChild('emailInput') emailInput!: ElementRef;
  @Input() emitOnSubmit: number = 0;
  @Input() removeOnBlur: boolean = false;
  @Output() formResult = new EventEmitter<any>();
  @Output() formValidity = new EventEmitter<boolean>();

  subscriptionForm: FormGroup;
  isLoading = false;
  private debounceTimer: any;

  constructor(
    private fb: FormBuilder,
    private subscriptionService: SubscriptionService
  ) {
    this.subscriptionForm = this.fb.group({
      LastName: ['', Validators.required],
      Mail1ZipCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      StakeEmail: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnInit(): void {
    this.subscriptionForm.statusChanges.subscribe((status) => {
      this.formValidity.emit(status === 'VALID');
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['emitOnSubmit']?.currentValue) {
      this.submitForm();
    }
  }

  onInput() {
    if (!this.removeOnBlur) {
      clearTimeout(this.debounceTimer);

      this.debounceTimer = setTimeout(() => {
        this.emitFormStatus('inputChanged');
        if (this.subscriptionForm.valid) {
          this.submitForm();
        }
      }, 2000);
    }
  }

  onBlur(): void {
    if (!this.removeOnBlur) {
      clearTimeout(this.debounceTimer);
      this.submitForm();
    }
  }

  focusFirstInvalidControl(form: FormGroup): void {
    for (const key of Object.keys(form.controls)) {
      const control = form.get(key);
      if (control && control.invalid) {
        const element = document.getElementById(key);
        if (element) {
          element.focus();
          break;
        }
      }
    }
  }

  async submitForm(): Promise<void> {
    if (this.subscriptionForm.invalid) {
      // This works but when injected to comment form, it constantly causes focus back to first input
      // this.focusFirstInvalidControl(this.subscriptionForm);
    } else if (this.subscriptionForm.valid) {
      this.isLoading = true;
      try {
        const { LastName, Mail1ZipCode, StakeEmail } = this.subscriptionForm.value;
        const response = await lastValueFrom(
          this.subscriptionService.getMatchingStakeholders(LastName, Mail1ZipCode, StakeEmail)
        );

        // Sends back to the parent that the form was successfully submitted - parent handles success + data
        this.formResult.emit({
          status: 'success',
          data: response,
          formValues: this.subscriptionForm.value,
        });
      } catch (error) {
        console.error('Error during service call:', error);
        // Sends back to the parent that the form was not successfully submitted - parent handles error
        this.formResult.emit({
          status: 'error',
          data: error,
          formValues: this.subscriptionForm.value,
        });
      } finally {
        this.isLoading = false;
      }
    } else {
      this.formResult.emit({
        status: 'invalid',
        data: this.subscriptionForm.value,
      });
    }
  }

  private emitFormStatus(status: string, message?: string): void {
    const data = { ...this.subscriptionForm.value, message };
    this.formResult.emit({ status, data });
  }

  ngOnDestroy(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
}
